import { morph, MORPH_OPTIONS } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Product specifications block.
 *
 * Markup is rendered server-side in Liquid with the correct variant-level
 * metafield values. On variant change, Horizon's variant-picker fetches the
 * updated section HTML and dispatches a `productSelect` event; we pull our own
 * fresh element out of that HTML and morph ourselves. No client-side state.
 *
 * Rows carry a stable `data-key`, and we reuse the theme's MORPH_OPTIONS (which
 * rejects empty whitespace text nodes). Both are required because the row count
 * varies per variant — a bare `{ childrenOnly: true }` morph drifts on the
 * whitespace between rows and eventually empties the table.
 *
 * Modelled on assets/product-inventory.js.
 */
class ProductSpecifications extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Listen on document, not the closest section, so the block keeps working
    // wherever a merchant places it on the page. The event bubbles up from the
    // variant-picker; the guards in the handler ignore other products.
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles the product select event by morphing in the specs for the new variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        // Combined listing may swap the product; only update if this is our product.
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newSpecs = html.querySelector('product-specifications');
        if (!newSpecs) return;

        morph(this, newSpecs, {
          ...MORPH_OPTIONS,
          getNodeKey: (node) => (node instanceof HTMLElement ? node.dataset.key : undefined),
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-specifications] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-specifications')) {
  customElements.define('product-specifications', ProductSpecifications);
}
